import axiosHttpClient from '#helpers/axiosHttpClient.js'
import handleApiError from '#helpers/logging/handleApiError.js'
import Logger from '#helpers/logging/Logger.js'
import { url_modules_enabled } from '#src/constants/endpoints.js'
import { defaultExpiresTime, errorRetryTime, nowTime } from '#stores/storeDefaults.js'
import { useAuthorizationStore } from '#stores/useAuthStore.js'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'

export const useModulesStore = defineStore('modules', () => {
	const modules = useStorage('modules', [], sessionStorage)
	const loading = shallowRef(false)
	const expires = useStorage('modules-expires', null, sessionStorage)

	/**
	 * Check whether a certain module is currently enabled.
	 * @param {string} moduleName - The name of the module to check
	 * @returns {boolean} - Whether the given module is enabled
	 */
	function isEnabled(moduleName) {
		fetchModules()
		const moduleExists = modules?.value?.find?.(module => module.name === moduleName) ?? false
		return moduleExists?.enabled === true
	}

	const list = computed(() => {
		return modules?.value ?? []
	})

	/**
	 * Fetch all the modules.
	 * @param {boolean} [force] - Whether to force fetching the modules
	 * @returns {Promise} - A promise to act upon
	 */
	async function fetchModules(force = false) {
		if (force) {
			loading.value = false
		}
		if (loading.value) {
			return
		}
		if (new Date().getTime() < expires.value && !force) {
			return
		}
		const auth = useAuthorizationStore()
		if (!auth.accessToken) {
			return
		}

		loading.value = true
		await axiosHttpClient
			.get(url_modules_enabled)
			.then(response => {
				modules.value = response?.data?.data ?? []
				expires.value = nowTime() + defaultExpiresTime
			})
			.catch(e => {
				Logger.debug('useModulesStore: fetch failed')
				expires.value = nowTime() + errorRetryTime
				handleApiError(e)
			})
			.finally(() => {
				loading.value = false
			})
	}

	/**
	 * Clean up the data.
	 */
	function remove() {
		loading.value = false
		modules.value = []
		expires.value = 0
	}

	return { modules, loading, expires, isEnabled, list, fetch: fetchModules, remove }
})
